import React, { useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo, useId } from 'react';
import { createPortal } from 'react-dom';
import Button from './Button';
import {
  getPlayerNameColor,
  getPlayerFullName,
  playerMatchesSearchQuery,
  formatSessionGamesCountLabel,
} from '../../utils/playerUtils';
import { getGenderTextColor } from '../../utils/genderColors';
import PlayerPickerSheet from './PlayerPickerSheet';
import { AddNewGuestRow, parseSearchToGuestName } from './AddNewGuestSearchRow';
import {
  mobileSearchInputAttrs,
  refocusMobileSearchInput,
  measureSearchDropdownPosition,
  preventInputBlurOnPointerDown,
  isCoarsePointer,
  isMobileSearchSheet,
} from '../../utils/mobileSearchInput';

function playerListDisplayName(player) {
  return getPlayerFullName(player);
}

function queuePositionFor(player, queueOrderIds) {
  if (!player?.id || !Array.isArray(queueOrderIds) || queueOrderIds.length === 0) return null;
  const index = queueOrderIds.indexOf(player.id);
  return index >= 0 ? index + 1 : null;
}

/**
 * Style for the portalled dropdown panel.
 * 'above' placement anchors by computed top-minus-height (not CSS `bottom`) so the panel's
 * bottom edge stays glued to the input regardless of viewport/keyboard quirks — `dropdownPosition.top`
 * for 'above' is the input's top edge, and `panelHeight` is the panel's actual rendered height
 * (measured via ResizeObserver), so shorter filtered lists never leave a gap or float away.
 */
function dropdownPanelStyle(dropdownPosition, panelHeight) {
  const base = {
    position: 'fixed',
    left: dropdownPosition.left,
    width: dropdownPosition.width,
    zIndex: 2000,
    maxHeight: dropdownPosition.maxHeight,
  };

  if (dropdownPosition.placement === 'above') {
    const height = panelHeight ?? dropdownPosition.maxHeight;
    return {
      ...base,
      top: dropdownPosition.top - height,
    };
  }

  return {
    ...base,
    top: dropdownPosition.top,
  };
}

/**
 * Rows shared by the desktop portalled dropdown and the mobile `PlayerPickerSheet`.
 * Assumes `filteredPlayers.length > 0` — callers render their own empty state.
 */
function PlayerSearchResultsList({
  filteredPlayers,
  partitionMatches,
  onSelectPlayer,
  playersInScheduledGames,
  showGamesPlayed,
  showStatus,
  includePaymentAliases,
  coarsePointer,
  queueOrderIds = null,
  mutedSectionLabel = 'Needs partner — tap to select',
  blockedSectionLabel = 'Already on a team',
}) {
  const { primary, mutedSelectable, blocked } = partitionMatches(filteredPlayers);
  const showQueuePosition = Array.isArray(queueOrderIds) && queueOrderIds.length > 0;
  const rowClass = coarsePointer
    ? 'w-full text-left p-3 min-h-[44px] touch-manipulation hover:bg-surface-subtle border-b border-border last:border-b-0 transition-colors'
    : 'w-full text-left p-2 hover:bg-surface-subtle border-b border-border last:border-b-0 transition-colors';

  const renderExtraMeta = (player) =>
    !showQueuePosition &&
    (showGamesPlayed || showStatus || (includePaymentAliases && player.payment_search_aliases?.length > 0)) && (
      <div className="text-sm text-text-secondary">
        {showGamesPlayed && `${player.gamesPlayedThisSession || 0} games`}
        {showGamesPlayed && showStatus && ' • '}
        {showStatus && player.status}
        {includePaymentAliases && player.payment_search_aliases?.length > 0 && (
          <span
            className="block text-xs text-text-muted mt-0.5 truncate"
            title={player.payment_search_aliases.join(', ')}
          >
            Bank names: {player.payment_search_aliases.join(', ')}
          </span>
        )}
      </div>
    );

  const rowScheduled = (player) =>
    playersInScheduledGames.has(player.id) && (
      <span className="ml-2 text-xs text-warning font-semibold">📅 Already Scheduled</span>
    );

  const renderNameRow = (player, { muted = false } = {}) => {
    const position = queuePositionFor(player, queueOrderIds);
    const nameClass = muted
      ? 'font-medium text-text-muted'
      : `font-medium ${getPlayerNameColor(player.gender)}`;
    return (
      <div className="flex items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-1.5">
          {showQueuePosition && position != null ? (
            <span
              className="w-5 shrink-0 text-right text-xs font-semibold tabular-nums text-text-muted"
              title={`Queue position ${position}`}
            >
              {position}
            </span>
          ) : null}
          <div className={`min-w-0 truncate ${nameClass}`}>
            {playerListDisplayName(player)}
            {rowScheduled(player)}
          </div>
        </div>
        {showQueuePosition && showGamesPlayed ? (
          <span className="shrink-0 text-sm text-text-muted" title="Games this session">
            {formatSessionGamesCountLabel(player)}
          </span>
        ) : null}
      </div>
    );
  };

  return (
    <>
      {primary.map((player) => (
        <button
          key={player.id}
          type="button"
          role="option"
          onMouseDown={preventInputBlurOnPointerDown}
          onClick={() => onSelectPlayer(player)}
          className={rowClass}
        >
          <div>
            {renderNameRow(player)}
            {renderExtraMeta(player)}
          </div>
        </button>
      ))}

      {mutedSelectable.length > 0 && (
        <>
          {mutedSectionLabel ? (
            <div className="px-2 py-1.5 text-xs font-semibold text-text-secondary bg-surface-subtle border-y border-border">
              {mutedSectionLabel}
            </div>
          ) : null}
          {mutedSelectable.map(({ player, reason }) => (
            <button
              key={player.id}
              type="button"
              role="option"
              onMouseDown={preventInputBlurOnPointerDown}
              onClick={() => onSelectPlayer(player)}
              className={`${rowClass} bg-surface-subtle/70 hover:bg-surface-subtle opacity-95`}
            >
              <div>
                {renderNameRow(player)}
                <div className="text-xs text-text-muted mt-0.5">{reason}</div>
                {renderExtraMeta(player)}
              </div>
            </button>
          ))}
        </>
      )}

      {blocked.length > 0 && (
        <>
          {blockedSectionLabel ? (
            <div className="px-2 py-1.5 text-xs font-semibold text-text-secondary bg-surface-subtle border-y border-border">
              {blockedSectionLabel}
            </div>
          ) : null}
          {blocked.map(({ player, reason }) => (
            <div
              key={player.id}
              role="option"
              aria-disabled="true"
              className="w-full text-left p-3 min-h-[44px] border-b border-border bg-surface-subtle/50 opacity-60 cursor-not-allowed select-none"
            >
              {renderNameRow(player, { muted: true })}
              <div className="text-xs text-text-muted mt-0.5">{reason}</div>
            </div>
          ))}
        </>
      )}
    </>
  );
}

/** Desktop-only: portalled, fixed-position dropdown anchored under/above the input. */
function PlayerSearchDropdown({
  portalRef,
  dropdownPosition,
  panelHeight,
  filteredPlayers,
  searchTerm,
  listboxId,
  onAddNewGuest,
  showNoMatchGuestRow,
  showGuestButton,
  guestActionButton,
  ...resultsProps
}) {
  const panelStyle = dropdownPanelStyle(dropdownPosition, panelHeight);

  if (filteredPlayers.length === 0) {
    return (
      <div
        ref={portalRef}
        id={listboxId}
        role="listbox"
        style={panelStyle}
        className="mt-0 overflow-y-auto overscroll-contain bg-surface border border-border rounded shadow-lg p-2 text-text-muted text-sm"
      >
        {showGuestButton && !showNoMatchGuestRow ? (
          <div className="mb-2">{guestActionButton}</div>
        ) : null}
        {showNoMatchGuestRow ? (
          <AddNewGuestRow search={searchTerm} onAddNewGuest={onAddNewGuest} />
        ) : (
          <div className="px-2 py-1">{searchTerm ? 'No players found' : 'No players available'}</div>
        )}
      </div>
    );
  }

  return (
    <div
      ref={portalRef}
      id={listboxId}
      role="listbox"
      style={panelStyle}
      className="mt-0 overflow-y-auto overscroll-contain bg-surface border border-border rounded shadow-lg"
    >
      {showGuestButton && !showNoMatchGuestRow ? (
        <div className="border-b border-border p-2">{guestActionButton}</div>
      ) : null}
      <PlayerSearchResultsList filteredPlayers={filteredPlayers} {...resultsProps} />
    </div>
  );
}

/**
 * Reusable player search combobox. Desktop shows a portalled, keyboard/scroll-aware dropdown;
 * mobile/narrow viewports (`isMobileSearchSheet`) open a full-width `PlayerPickerSheet` instead,
 * so results always stay above the virtual keyboard and never detach from the input.
 */
const PlayerSearchInput = ({
  placeholder = 'Search players...',
  availablePlayers = [],
  selectedPlayer = null,
  onSelectPlayer,
  onRemovePlayer,
  disabled = false,
  showGamesPlayed = false,
  showStatus = false,
  showSelectedInfo = false,
  playersInScheduledGames = new Set(),
  includePaymentAliases = false,
  className = '',
  getPlayerDisabledReason = null,
  getPlayerMutedReason = null,
  /** Opens Add guest flow; receives { firstName, lastName, searchQuery } from search context. */
  onAddNewGuest = null,
  /** When set, rows show 1-based waitlist position (and games on the right if `showGamesPlayed`). */
  queueOrderIds = null,
  mutedSectionLabel,
  blockedSectionLabel,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [coarsePointer] = useState(() => isCoarsePointer());
  // Mobile/narrow viewports open a full-width PlayerPickerSheet instead of a floating dropdown —
  // avoids the keyboard covering results and the panel detaching from the input while typing.
  const [mobileSheetMode] = useState(() => isMobileSearchSheet());
  const searchInputRef = useRef(null);
  const dropdownRef = useRef(null);
  const dropdownPortalRef = useRef(null);
  const listboxId = useId();
  const [dropdownPosition, setDropdownPosition] = useState(null);
  const [panelHeight, setPanelHeight] = useState(null);

  const filteredPlayers = useMemo(() => {
    if (!searchTerm.trim()) {
      return availablePlayers;
    }
    return availablePlayers.filter((player) =>
      playerMatchesSearchQuery(player, searchTerm, { includePaymentAliases })
    );
  }, [searchTerm, availablePlayers, includePaymentAliases]);

  // Anchor to the input itself (not the wrapper) so selected-info blocks or custom
  // wrapper classNames never shift the measured position.
  const updateDropdownPosition = useCallback(() => {
    const next = measureSearchDropdownPosition(searchInputRef.current);
    if (next) setDropdownPosition(next);
  }, []);

  const openDropdown = useCallback(() => {
    const next = measureSearchDropdownPosition(searchInputRef.current);
    if (next) setDropdownPosition(next);
    setShowDropdown(true);
  }, []);

  // Re-measure on open and whenever the filtered result count changes — a shorter list
  // changes the available space calculation and (for 'above' placement) the panel height.
  // Skipped in mobile sheet mode, which doesn't need floating-panel geometry.
  useLayoutEffect(() => {
    if (!showDropdown || mobileSheetMode) return;
    updateDropdownPosition();
  }, [showDropdown, mobileSheetMode, filteredPlayers.length, updateDropdownPosition]);

  useEffect(() => {
    if (!showDropdown || mobileSheetMode) return;

    let raf = 0;
    const schedule = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(updateDropdownPosition);
    };

    window.addEventListener('resize', schedule);
    window.visualViewport?.addEventListener('resize', schedule);
    window.visualViewport?.addEventListener('scroll', schedule);
    // Capture phase catches scroll from any scrollable ancestor (modals, panels), not just window.
    document.addEventListener('scroll', schedule, true);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', schedule);
      window.visualViewport?.removeEventListener('resize', schedule);
      window.visualViewport?.removeEventListener('scroll', schedule);
      document.removeEventListener('scroll', schedule, true);
    };
  }, [showDropdown, mobileSheetMode, updateDropdownPosition]);

  // Track the panel's actual rendered height so 'above' placement can anchor by its
  // bottom edge (top = anchorTop - height) instead of a CSS `bottom` value, which is
  // unreliable across browsers once the virtual keyboard resizes the viewport.
  useEffect(() => {
    if (!showDropdown || mobileSheetMode || dropdownPosition?.placement !== 'above') return;
    const el = dropdownPortalRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;

    const observer = new ResizeObserver((entries) => {
      const height = entries[0]?.contentRect?.height;
      if (height != null) setPanelHeight(height);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [showDropdown, mobileSheetMode, dropdownPosition?.placement, filteredPlayers.length]);

  // Desktop only — the mobile PlayerPickerSheet manages its own outside-click/escape dismissal
  // (it portals to <body>, so it wouldn't be considered "inside" dropdownRef/dropdownPortalRef).
  useEffect(() => {
    if (mobileSheetMode) return;
    const handlePointerDown = (event) => {
      const target = event.target;
      if (searchInputRef.current === target) return;
      const clickedInsideTrigger = dropdownRef.current?.contains(target);
      const clickedInsideDropdown = dropdownPortalRef.current?.contains(target);
      if (!clickedInsideTrigger && !clickedInsideDropdown) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener('pointerdown', handlePointerDown);
      return () => document.removeEventListener('pointerdown', handlePointerDown);
    }
  }, [showDropdown, mobileSheetMode]);

  useEffect(() => {
    if (mobileSheetMode) return;
    const handleEscape = (e) => {
      if (e.key === 'Escape' && showDropdown) {
        setShowDropdown(false);
        searchInputRef.current?.blur();
      }
    };

    if (showDropdown) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [showDropdown, mobileSheetMode]);

  useEffect(() => {
    if (!showDropdown) setPanelHeight(null);
  }, [showDropdown]);

  const handlePlayerSelect = (player) => {
    const blockReason = getPlayerDisabledReason?.(player);
    if (blockReason) return;
    onSelectPlayer?.(player);
    setSearchTerm('');
    setShowDropdown(false);
  };

  const partitionMatches = useCallback(
    (list) => {
      const primary = [];
      const mutedSelectable = [];
      const blocked = [];
      for (const player of list) {
        const disabledReason = getPlayerDisabledReason?.(player) || null;
        const mutedReason = getPlayerMutedReason?.(player) || null;
        if (disabledReason) {
          blocked.push({ player, reason: disabledReason });
        } else if (mutedReason) {
          mutedSelectable.push({ player, reason: mutedReason });
        } else {
          primary.push(player);
        }
      }
      return { primary, mutedSelectable, blocked };
    },
    [getPlayerDisabledReason, getPlayerMutedReason]
  );

  const handleRemoveSelected = () => {
    onRemovePlayer?.();
    setSearchTerm('');
  };

  const handleInputFocus = () => {
    openDropdown();
    if (mobileSheetMode) {
      // The trigger is `readOnly` on mobile so it never invites the OS keyboard in the first
      // place (typing happens in the sheet's own search field) — this also sidesteps any
      // chance of it zooming the page on focus. Still blur defensively in case some browser
      // focuses/carets a readOnly input in a way that keeps it "active".
      requestAnimationFrame(() => {
        if (document.activeElement === searchInputRef.current) {
          searchInputRef.current?.blur();
        }
      });
    }
  };

  const handleInputChange = (e) => {
    setSearchTerm(e.target.value);
    if (!showDropdown) {
      openDropdown();
    } else if (!mobileSheetMode) {
      updateDropdownPosition();
    }
    if (!mobileSheetMode) {
      refocusMobileSearchInput(searchInputRef);
    }
  };

  const showResults = showDropdown && !selectedPlayer;
  const desktopDropdownReady = showResults && !mobileSheetMode && dropdownPosition?.width > 0;
  const dropdownReady = mobileSheetMode ? showResults : desktopDropdownReady;

  const searchTrimmed = searchTerm.trim();
  const showNoMatchGuestRow = Boolean(onAddNewGuest) && Boolean(searchTrimmed) && filteredPlayers.length === 0;
  const showGuestButton = Boolean(onAddNewGuest);

  const handleAddNewGuest = useCallback(
    (prefill) => {
      setShowDropdown(false);
      setSearchTerm('');
      onAddNewGuest?.(prefill);
    },
    [onAddNewGuest]
  );

  const guestActionButton = showGuestButton && !showNoMatchGuestRow ? (
    <Button
      type="button"
      variant="outline"
      size="small"
      className="w-full min-h-[44px] touch-manipulation sm:min-h-0"
      onMouseDown={preventInputBlurOnPointerDown}
      onClick={() => {
        const { firstName, lastName } = parseSearchToGuestName(searchTerm);
        handleAddNewGuest({ firstName, lastName, searchQuery: searchTrimmed });
      }}
    >
      + Add new guest player
    </Button>
  ) : null;

  const mobileHasResults = filteredPlayers.length > 0 || showNoMatchGuestRow || showGuestButton;

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <div className="relative">
        <input
          ref={searchInputRef}
          type="text"
          role="combobox"
          aria-expanded={dropdownReady}
          aria-controls={dropdownReady ? listboxId : undefined}
          aria-autocomplete="list"
          placeholder={placeholder}
          value={
            selectedPlayer
              ? [
                  queuePositionFor(selectedPlayer, queueOrderIds),
                  playerListDisplayName(selectedPlayer),
                ]
                  .filter((part) => part != null && part !== '')
                  .join(' ')
              : searchTerm
          }
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          readOnly={mobileSheetMode}
          disabled={disabled || selectedPlayer}
          className={`form-input pr-8 ${
            selectedPlayer ? `${getGenderTextColor(selectedPlayer.gender)} font-medium` : ''
          }`}
          {...mobileSearchInputAttrs}
        />

        <div className="absolute inset-y-0 right-0 pr-2 flex items-center pointer-events-none">
          {selectedPlayer ? (
            <button
              onMouseDown={preventInputBlurOnPointerDown}
              onClick={handleRemoveSelected}
              className="text-danger-dark hover:text-danger text-lg font-bold pointer-events-auto cursor-pointer"
              type="button"
              title="Remove player"
            >
              ×
            </button>
          ) : (
            <svg className="h-4 w-4 text-text-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          )}
        </div>
      </div>

      {selectedPlayer && showSelectedInfo && (
        <div className="mt-2 p-2 bg-info-light rounded border border-info">
          <div className="flex justify-between items-center">
            <div>
              <span className="font-medium text-text-primary">{playerListDisplayName(selectedPlayer)}</span>
              {showGamesPlayed && (
                <span className="text-sm text-text-secondary ml-2">
                  ({selectedPlayer.gamesPlayedThisSession || 0} games)
                </span>
              )}
              {showStatus && selectedPlayer.status && (
                <span className="text-xs text-text-secondary ml-2">• {selectedPlayer.status}</span>
              )}
            </div>
          </div>
        </div>
      )}

      {mobileSheetMode ? (
        <PlayerPickerSheet
          isOpen={dropdownReady}
          onClose={() => setShowDropdown(false)}
          title="Select player"
          searchTerm={searchTerm}
          onSearchChange={handleInputChange}
          searchPlaceholder={placeholder}
          hasResults={mobileHasResults}
          emptyMessage={searchTerm ? 'No players found' : 'No players available'}
          belowSearch={guestActionButton}
        >
          <PlayerSearchResultsList
            filteredPlayers={filteredPlayers}
            partitionMatches={partitionMatches}
            onSelectPlayer={handlePlayerSelect}
            playersInScheduledGames={playersInScheduledGames}
            showGamesPlayed={showGamesPlayed}
            showStatus={showStatus}
            includePaymentAliases={includePaymentAliases}
            coarsePointer={coarsePointer}
            queueOrderIds={queueOrderIds}
            mutedSectionLabel={mutedSectionLabel}
            blockedSectionLabel={blockedSectionLabel}
          />
          {showNoMatchGuestRow ? (
            <AddNewGuestRow search={searchTerm} onAddNewGuest={handleAddNewGuest} className="mt-1" />
          ) : null}
        </PlayerPickerSheet>
      ) : (
        dropdownReady &&
        createPortal(
          <PlayerSearchDropdown
            portalRef={dropdownPortalRef}
            dropdownPosition={dropdownPosition}
            panelHeight={panelHeight}
            filteredPlayers={filteredPlayers}
            searchTerm={searchTerm}
            partitionMatches={partitionMatches}
            onSelectPlayer={handlePlayerSelect}
            playersInScheduledGames={playersInScheduledGames}
            showGamesPlayed={showGamesPlayed}
            showStatus={showStatus}
            includePaymentAliases={includePaymentAliases}
            listboxId={listboxId}
            coarsePointer={coarsePointer}
            queueOrderIds={queueOrderIds}
            mutedSectionLabel={mutedSectionLabel}
            blockedSectionLabel={blockedSectionLabel}
            onAddNewGuest={handleAddNewGuest}
            showNoMatchGuestRow={showNoMatchGuestRow}
            showGuestButton={showGuestButton}
            guestActionButton={guestActionButton}
          />,
          document.body
        )
      )}
    </div>
  );
};

export default PlayerSearchInput;
